import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableFooter,
    Paper,
    FormControl,
    FormControlLabel,
    Switch,
} from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';

import { Block, ActionButton, useKlpStyles, LocalLoader } from '@klp/design';
import type {
    Kostnadstype,
    KostnadstypeRequest,
} from '@klp/prosjekt/services/types';
import { useKostnadsKalkyleContext } from '../kostnadskalkyle/context/KostnadsKalkyleProvider';
import { KostnadstyperTableRow } from './KostnadstyperTableRow';

const styles = (theme: Theme) => ({
    filterContainer: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.grey[300]}`,
    },
    tableHead: {
        backgroundColor: theme.palette.background.paper,
    },
    cell: {
        '&.navn': { width: '30%' },
        '&.kostkategori': { width: '30%' },
        '&.defaultCost': { width: '15%' },
        '&.defaultKostnadsforingAndel': { width: '15%' },
        '&.actions': { width: '10%' },
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(2),
    },
});

const emptyKostnadstype: Kostnadstype = {
    id: 0,
    navn: '',
    kostKategoriId: '',
    defaultCost: 0,
    defaultKostnadsforingAndel: 0,
    deleted: false,
    created: new Date(),
    lastUpdated: null,
};

export const KostnadstyperTable: React.FC = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const {
        kostnadstyper,
        kostkategorier,
        upsertKostnadstype,
        refetchKostnadstyper,
        kostnadstyperLoading,
        kostkategorierLoading,
    } = useKostnadsKalkyleContext();

    const [showDeleted, setShowDeleted] = useState(true);
    const [editingRows, setEditingRows] = useState<Set<number>>(new Set());
    const [newRow, setNewRow] = useState<Kostnadstype>();

    const handleAddNew = () => {
        setNewRow(emptyKostnadstype);
    };

    const handleUpsertKostnadstype = async (data: KostnadstypeRequest) => {
        await upsertKostnadstype(data)
            .unwrap()
            .catch((_error) => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
        refetchKostnadstyper();
        setNewRow(undefined);
        setEditingRows((prev) => {
            if (data.id) {
                const newSet = new Set(prev);
                newSet.delete(data.id);
                return newSet;
            }
            return prev;
        });
    };

    const handleCancelNew = () => {
        setNewRow(undefined);
    };

    const handleEdit = (id: number) => {
        setEditingRows((prev) => new Set(prev).add(id));
    };

    const handleCancelEdit = (id: number) => {
        setEditingRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    const displayedKostnadstyper = useMemo(
        () =>
            showDeleted
                ? (kostnadstyper ?? [])
                : (kostnadstyper?.filter((kt) => !kt.deleted) ?? []),
        [kostnadstyper, showDeleted],
    );

    const isLoading = kostnadstyperLoading || kostkategorierLoading;

    return (
        <LocalLoader isLoading={isLoading}>
            <Paper elevation={0} className={classes.filterContainer}>
                <FormControl component="fieldset">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showDeleted}
                                onChange={(event) =>
                                    setShowDeleted(event.target.checked)
                                }
                                name="showDeleted"
                            />
                        }
                        label={t('prosjekt.kostnadstyper.showDeleted')}
                    />
                </FormControl>
            </Paper>
            <Paper>
                <Table>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={`${classes.cell} navn`}>
                                {t('prosjekt.kostnadstyper.table.navn')}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} kostkategori`}
                            >
                                {t('prosjekt.kostnadstyper.table.kostkategori')}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} defaultCost`}
                            >
                                {t('prosjekt.kostnadstyper.table.defaultCost')}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} defaultKostnadsforingAndel`}
                            >
                                {t(
                                    'prosjekt.kostnadstyper.table.defaultKostnadsforingAndel',
                                )}
                            </TableCell>
                            <TableCell
                                className={`${classes.cell} actions`}
                                aria-label="Handlinger"
                            />
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {displayedKostnadstyper.map((kostnadstype) => (
                            <KostnadstyperTableRow
                                key={kostnadstype.id}
                                data={kostnadstype}
                                kostkategorier={kostkategorier ?? []}
                                isEditing={editingRows.has(kostnadstype.id)}
                                onEdit={() => handleEdit(kostnadstype.id)}
                                onUpsert={handleUpsertKostnadstype}
                                onCancel={() =>
                                    handleCancelEdit(kostnadstype.id)
                                }
                                isNew={false}
                            />
                        ))}
                        {newRow && (
                            <KostnadstyperTableRow
                                data={newRow}
                                kostkategorier={kostkategorier ?? []}
                                isEditing={true}
                                onUpsert={handleUpsertKostnadstype}
                                onCancel={handleCancelNew}
                                isNew={true}
                            />
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={5}>
                                <Block className={classes.footer}>
                                    <ActionButton
                                        actionType="button"
                                        onClick={handleAddNew}
                                        disabled={Boolean(newRow)}
                                    >
                                        {t('prosjekt.kostnadstyper.table.add')}
                                    </ActionButton>
                                </Block>
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </Paper>
        </LocalLoader>
    );
};
